import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface TransportCostsFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
}

const state: TransportCostsFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    meta: {},
};

const initState: TransportCostsFormState = cloneDeep(state);

export const LINES_DATA = 'GET_LINES_DATA';
export const LINES_SCHEMA = 'GET_LINES_SCHEMA';

export const actions: ActionTree<TransportCostsFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(LINES_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', result.getOperation(LINES_DATA).getContent());

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(LINES_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:voc_lines`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    schema: result.getOperation(LINES_SCHEMA).getContent(),
                    initParams,
                    defs: {}
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, params) {
        const vm = (this as any)._vm;

        const normalizedForm = state.schema.fields.reduce((prev: any, field: any) => {
            const fieldName = field.code;

            if (state.form[fieldName]) {
                switch(field.type) {
                    case 'date': {
                        prev[fieldName] = moment(state.form[fieldName]).utc().local().format('YYYY-MM-DD');
                        break;
                    }
                    default: {
                        prev[fieldName] = state.form[fieldName]
                    }
                }

            }

            return prev
        }, {});

        console.log('normalizedForm', normalizedForm);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (params.id) {
                saveQuery = vm.$q('LINES_DATA_SAVE')
                    .update(params.id, normalizedForm)
            } else {
                saveQuery = vm.$q('LINES_DATA_SAVE')
                    .create('s:voc_lines', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('LINES_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

};

export const mutations: MutationTree<TransportCostsFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_LINES_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams, defs}) {
        state.document = {};
        state.schema = schema.schema;
        state.meta = fetchEnumsFromSchema(schema.schema);

        const formData = state.schema.fields.reduce((prev: any, field: any) => {
            if (initParams[field.code]) {
                if(['date', 'datetime'].includes(field.type)) {
                    prev[field.code] = moment(initParams[field.code]).toDate();
                } else {
                    prev[field.code] = initParams[field.code];
                }
            } else if (defs[field.code]) {
                if(['date', 'datetime'].includes(field.type)) {
                    prev[field.code] = moment(defs[field.code]).toDate();
                } else {
                    prev[field.code] = defs[field.code];
                }
            }
            else {
                prev[field.code] = null;
            }

            return prev;
        }, {})

        state.form = formData;
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: TransportCostsFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: TransportCostsFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_line_manager_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
